//Global imports
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import { CButton } from '@coreui/react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

//File imports
import { Language } from '../../../../language/English';
import { appDispatch, RootState } from '../../../../app/store';
import {
    addFaq,
    updateFaqContent,
    removeFaq,
    manageFaqPosition,
    addImageInFaq,
    updateFaqIdWithNote,
} from './Redux/faqsSlice';
import { DocumentTemplateTranslationService } from '../../Api/DocumentTemplateTranslationService';
import { DOCUMENT_TEMPLATE_TRANSLATION_SUBFOLDER_TYPE_FAQ } from '../../../../Constant';
import Logger from '../../../../services/Logger/Logger';
import Notification from '../../../../services/Notification/Notification';
import { DocumentTemplateTranslationImageUploadPojo } from '../../Api/DocumentTemplateTranslationImageUploadPojo';

//Component imports
import FAQItem from '../FAQItem';

interface FormErrors {
    faqs?: string;
}

type FaqsProps = {
    folderName: string | null;
    setLoader: (status: boolean) => void;
    setUniqueFolderName?: (name: string) => void;
    faqValidationError?: string;
    showFaqNote: boolean;
    setShowFaqNote: (status: boolean) => void;
};

const FAQs: React.FC<FaqsProps> = ({
    folderName,
    setLoader = (status: boolean) => {},
    setUniqueFolderName = (name: string) => {},
    faqValidationError,
    showFaqNote,
    setShowFaqNote = (status: boolean) => {},
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );
    const faqs = useSelector((state: RootState) => state.faqsReducer.faqs);
    const dispatch = useDispatch<appDispatch>();
    const notification = new Notification();
    const logger = new Logger();
    const documentTemplateService = new DocumentTemplateTranslationService();

    const [emptyLastFaq, setEmptyLastFaq] = useState<boolean>(false);
    const [errors, setErrors] = useState<FormErrors>(
        faqValidationError ? { faqs: faqValidationError } : {},
    );

    // to remove faq from list
    const removeFaqHandler = (id: string) => {
        dispatch(removeFaq(id));
        setEmptyLastFaq(false);
        setErrors({});
        dispatch(updateFaqIdWithNote(null));
        setShowFaqNote(true);
    };

    // when question and answer field of faq get changed, this function will be called
    const handleFaqChange = (id: string, field: string, value: string) => {
        faqs.map((faq) => {
            if (faq.id === id) {
                dispatch(updateFaqContent({ ...faq, [field]: value }));
            }
        });
        dispatch(updateFaqIdWithNote(id));
    };

    // to add new faq in list
    const addNewFaq = () => {
        if (faqs.length > 0) {
            const lastFaq = faqs[faqs.length - 1];
            let allFaqsIds = faqs.map((item) => Number(item.id));
            let maxId = Math.max(...allFaqsIds);
            if (
                lastFaq.question.trim() !== '' &&
                lastFaq.answer.trim() !== ''
            ) {
                setEmptyLastFaq(false);
                dispatch(
                    addFaq({
                        id: `${maxId + 1}`,
                        question: '',
                        answer: '',
                        image: null,
                    }),
                );
                let { faqs: f, ...newErrors } = errors;
                setErrors(newErrors);
            } else {
                setEmptyLastFaq(true);
                return false;
            }
        } else {
            let { faqs: f, ...newErrors } = errors;
            setErrors(newErrors);
            dispatch(
                addFaq({ id: '1', question: '', answer: '', image: null }),
            );
        }
    };

    // when faq drag and drop for change position, this function will be called
    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = faqs.findIndex((item) => item.id === active.id);
            const newIndex = faqs.findIndex((item) => item.id === over.id);
            const newData = arrayMove(faqs, oldIndex, newIndex);
            dispatch(manageFaqPosition(newData));
        }
        setShowFaqNote(true);
    };

    // to upload image to server
    const uploadImageToServer = async (
        id: string,
        file: File,
    ): Promise<void> => {
        try {
            setLoader(true);
            let payload = {
                folderName: folderName,
                type: DOCUMENT_TEMPLATE_TRANSLATION_SUBFOLDER_TYPE_FAQ,
                previewImage: file,
            };

            const { success, message, data } =
                await documentTemplateService.uploadImage(payload);

            const {
                uniqueFolderName,
                fileName,
                filePath,
                width,
                height,
                fileSizeInKb,
            }: DocumentTemplateTranslationImageUploadPojo = data;
            if (success) {
                setUniqueFolderName(uniqueFolderName);
                dispatch(
                    addImageInFaq({
                        id,
                        image: filePath,
                        fileName,
                        height,
                        width,
                        fileSizeInKb,
                    }),
                );
                dispatch(updateFaqIdWithNote(id));
            } else {
                notification.error(message ?? Language.UNABLE_TO_IMAGE);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className="mt-4">
            <h5>{Language.FAQS}</h5>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={faqs.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {faqs &&
                        Array.isArray(faqs) &&
                        faqs.length > 0 &&
                        faqs.map((faq, index) => (
                            <FAQItem
                                key={index}
                                id={faq.id}
                                emptyLastFaq={emptyLastFaq}
                                content={faq}
                                length={faqs.length}
                                lastIndex={faqs.length - 1 === index}
                                onTextChange={handleFaqChange}
                                onRemoveFaq={removeFaqHandler}
                                onImageChange={uploadImageToServer}
                            />
                        ))}
                </SortableContext>
            </DndContext>
            {faqValidationError && (
                <div className="text-danger mb-2">{Language.FAQS_REQUIRED}</div>
            )}

            {showFaqNote && (
                <div className="alert alert-danger">
                    {Language.UPLOAD_FAQ_NOTE}
                </div>
            )}

            <CButton color="primary" onClick={addNewFaq}>
                {Language.ADD_FAQ}
            </CButton>
        </div>
    );
};

export default FAQs;
